import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createJob,
  fetchCoreEducation,
  fetchCoreLocation,
  fetchCoreSkills,
  fetchSuggestedSkills,
  saveSkills,
} from './createJobAPI';

const initialState = {
  value: 0,
  status: 'idle',
  createJobResponse: {},
  coreEducations: null,
  coreLocation: null,
  coreSkills: null,
  selectedSkills: null,
  saveSkillsRes: null,
  error: null,
};

export const createJobAsync = createAsyncThunk(
  'employer/jobCreate',
  async ({ data }, { rejectWithValue }) => {
    try {
      const res = await createJob(data);

      // If the API uses a success flag inside 200 responses
      if (res?.success === false || res?.status === false) {
        return rejectWithValue(res);
      }

      return res;
    } catch (err) {
      return rejectWithValue({
        message: err?.message || 'Failed to post job',
        status: err?.status,
        data: err?.data,
      });
    }
  }
);

export const fetchCoreEducationAsync = createAsyncThunk(
  'employer/jobFetchCoreEducation',
  async () => {
    const response = await fetchCoreEducation();
    return response;
  }
);

export const fetchCoreLocationAsync = createAsyncThunk(
  'employer/jobFetchCoreLocation',
  async () => {
    const response = await fetchCoreLocation();
    return response;
  }
);

export const fetchCoreSkillsAsync = createAsyncThunk(
  'employer/jobFetchCoreSkills',
  async () => {
    const response = await fetchCoreSkills();
    return response;
  }
);

// Updated: accept keyword only (no token from component)
export const fetchSuggestedSkillsAsync = createAsyncThunk(
  'employer/jobFetchSuggestedSkills',
  async (keyword = "") => {
    const response = await fetchSuggestedSkills(keyword);
    return response;
  }
);

export const saveSkillsAsync = createAsyncThunk(
  'employer/saveSkills',
  async ({ accessToken, data }) => {
    const response = await saveSkills(accessToken, data);
    return response;
  }
);

export const createJobSlice = createSlice({
  name: 'employerJobCreate',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create Job
      .addCase(createJobAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createJobAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.createJobResponse = action.payload;
        state.error = null;
      })
      .addCase(createJobAsync.rejected, (state, action) => {
        state.status = 'idle';
        state.error = action.payload?.message || action.error?.message || 'Failed to post job';
        state.createJobResponse = action.payload || {};
      })

      // Core Education
      .addCase(fetchCoreEducationAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCoreEducationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.coreEducations = action.payload;
      })

      // Core Location
      .addCase(fetchCoreLocationAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCoreLocationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.coreLocation = action.payload;
      })

      // Core Skills
      .addCase(fetchCoreSkillsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCoreSkillsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.coreSkills = action.payload;
      })

      // Suggested Skills
      .addCase(fetchSuggestedSkillsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSuggestedSkillsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.selectedSkills = action.payload;
      })

      // Save Skills
      .addCase(saveSkillsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(saveSkillsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.saveSkillsRes = action.payload;
      });
  },
});

export const selectCreateJobResponse = (state) => state.createJobReducers.createJobResponse;
export const selectCoreEducations = (state) => state.createJobReducers.coreEducations;
export const selectCoreLocation = (state) => state.createJobReducers.coreLocation;
export const selectCoreSkills = (state) => state.createJobReducers.coreSkills;
export const selectSelectedSkills = (state) => state.createJobReducers.selectedSkills;
export const selectedSaveSkillsRes = (state) => state.createJobReducers.saveSkillsRes;

export default createJobSlice.reducer;